import { HOUR_DURATION } from './constants';

class Intervals {
  constructor(allIntervals) {
    this.allIntervals = allIntervals;
    this.byId = new Map();
    this.byFromTimes = new Map();
    this.byToTimes = new Map();
    this.fromTimes = [];
    this.toTimes = [];
    this._fromSorted = false;
    this._toSorted = false;

    this.add = this.add.bind(this);
    this.getTimes = this.getTimes.bind(this);
    this.isFromAvailableCreator = this.isFromAvailableCreator.bind(this);
    this.isToAvailableCreator = this.isToAvailableCreator.bind(this);
    this.createIntervals = this.createIntervals.bind(this);
    this.syncTimes = this.syncTimes.bind(this);
    this.syncTime = this.syncTime.bind(this);
    this.isTimeAvailableCreator = this.isTimeAvailableCreator.bind(this);
    this.addMissingIntervals = this.addMissingIntervals.bind(this);
  }

  add(interval) {
    this.byId.set(interval.id, interval);
    this.byFromTimes.set(interval.from.value, interval);
    this.byToTimes.set(interval.to.value, interval);
    this.fromTimes.push(interval.from);
    this.toTimes.push(interval.to);
  }

  getTimes(key) {
    const isTo = key === 'to';
    const sortedFlag = isTo ? '_toSorted' : '_fromSorted';
    const times = isTo ? this.toTimes : this.fromTimes;

    if (!this[sortedFlag]) {
      times.sort(this._compareMinutes);
      this[sortedFlag] = true;
    }

    return times;
  }

  // для классической настройки интервалов
  isFromAvailableCreator({ to, current }) {
    // current - текущее значение при редактировании конфигурации
    // цель условия для current: чтобы количество интервалов увеличивалось, а не уменьшалось

    return (time) => {
      const toOk = !to || to.minutes > time.minutes;
      const currentOk = !current || current.minutes >= time.minutes;

      return toOk && currentOk;
    };
  }

  // для классической настройки интервалов
  isToAvailableCreator({ from, current }) {
    // current - текущее значение при редактировании конфигурации
    // цель условия для current: чтобы количество интервалов увеличивалось, а не уменьшалось

    return (time) => {
      const fromOk = !from || from.minutes < time.minutes;
      const currentOk = !current || current.minutes <= time.minutes;

      return fromOk && currentOk;
    };
  }

  // для классической настройки интервалов
  createIntervals({ from, to, creator }) {
    if (!from || !to || typeof creator !== 'function') return;

    return this.getTimes('from').reduce((acc, time) => {
      const i = this.byId.get(time.context.id);

      if (i.from.minutes >= from.minutes && i.to.minutes <= to.minutes) {
        acc.push(creator(i));
      }

      return acc;
    }, []);
  }

  // для классической настройки интервалов
  syncTimes({ prevFrom, prevTo }) {
    return {
      from: this.byFromTimes.get(prevFrom?.value)?.from ?? null,
      to: this.byToTimes.get(prevTo?.value)?.to ?? null,
    };
  }

  // для по интервальной настроки интервалов
  syncTime({ prevTime, selectedIds }) {
    const nextInterval = this.byFromTimes.get(prevTime?.value);

    if (!nextInterval) return null;

    const isAvailale = this.isTimeAvailableCreator(
      selectedIds,
      (id) => id === prevTime?.context?.id
    );

    return isAvailale(nextInterval.from) ? nextInterval.from : null;
  }

  // для по интервальной настроки интервалов
  isTimeAvailableCreator(selectedIds, continueCondition) {
    return (time) => {
      if (!selectedIds?.size) return true;

      for (const id of selectedIds) {
        if (typeof continueCondition === 'function' && continueCondition(id)) {
          continue;
        }

        // const isSame = id === time.context.id;
        const hasIntersection = this.byId
          .get(time.context.id)
          .hasIntersection(this.allIntervals.get(id));

        // if (isSame || hasIntersection) return false; // проверить, если после тестирование такое повевдение норм, то удалить строку
        if (hasIntersection) return false;
      }

      return true;
    };
  }

  // для классической настройки интервалов
  addMissingIntervals({
    accumulator,
    currentInterval,
    nextIntervalId,
    adapter,
  }) {
    const nextInterval = this.byId.get(nextIntervalId);

    if (!nextInterval) return;

    let currentToValue = currentInterval.to.value;
    const nextFromValue = nextInterval.from.value;

    while (currentToValue !== nextFromValue) {
      const missInterval = this.byFromTimes.get(currentToValue);

      if (!missInterval) break;

      accumulator.push(adapter(missInterval));

      currentToValue = missInterval.to.value;
    }
  }

  _compareMinutes(a, b) {
    return a.minutes - b.minutes;
  }
}

class HourlyIntervals extends Intervals {
  _getCondition(time) {
    if (!time) return;

    const min = time.slice(-2);

    return (t) => t.endsWith(min);
  }

  isFromAvailableCreator({ to, current }) {
    return (time) => {
      const toOk =
        !to ||
        (time.value.endsWith(to.value.slice(-2)) && to.minutes > time.minutes);
      const currentOk = !current || current.minutes >= time.minutes;

      return toOk && currentOk;
    };
  }

  isToAvailableCreator({ from, current }) {
    // current - текущее значение при редактировании конфигурации
    // цель условия для current: чтобы количество интервалов увеличивалось, а не уменьшалось

    const min = from?.value?.slice(-2);

    return (time) => {
      const fromOk =
        !from || (time.value.endsWith(min) && from.minutes < time.minutes);
      const currentOk = !current || current.minutes <= time.minutes;

      return fromOk && currentOk;
    };
  }

  createIntervals({ from, to, creator }) {
    if (!from || !to || typeof creator !== 'function') return;

    const min = from.value.slice(-2);

    return this.getTimes('from').reduce((acc, time) => {
      const i = this.byId.get(time.context.id);

      if (
        i.from.value.endsWith(min) &&
        i.from.minutes >= from.minutes &&
        i.to.minutes <= to.minutes
      ) {
        acc.push(creator(i));
      }

      return acc;
    }, []);
  }

  syncTimes({ prevFrom, prevTo }) {
    const from = this.byFromTimes.get(prevFrom?.value)?.from ?? null;
    const to = this.byToTimes.get(prevTo?.value)?.to ?? null;

    if (from && to) {
      const fromMin = from.value.slice(-2);
      return { from, to: to.value.endsWith(fromMin) ? to : null };
    }

    return { from, to };
  }
}

export const createIntervalsInstance = (duration, allIntervals) =>
  duration === HOUR_DURATION
    ? new HourlyIntervals(allIntervals)
    : new Intervals(allIntervals);
